import type { CardCorners } from 'blocks/Cards'
import type { IBannerProps } from './Banner'

import { styled } from '@linaria/react'
import { ButtonSC } from 'atoms/Button'
import { FallbackImageSC } from 'atoms/FallbackImage'
import { IconSC, IconSizes } from 'atoms/Icon'
import { PosterSC } from 'atoms/ImagePoster'
import {
  PartnerProgramCoverDesktopSC,
  PartnerProgramCoverMobileSC,
} from 'atoms/PartnerProgramCover'
import { ButtonGroupWrapper } from 'blocks/ButtonGroup'
import {
  BANNER_ASPECT_RATIO,
  BANNER_BACK_BUTTON_INDENT,
  BANNER_HEIGHT,
  COLOR,
  CORNERS,
  FIXED_INDENTS,
  LAYOUT,
  MEDIA,
  NAVBAR_HEIGHT,
} from 'vars/index'
import {
  BannerAlignItems,
  BannerJustifyContent,
  BannerOverlay,
} from './Banner.h'

export const BannerContent = styled.div<{
  justifyContent?: IBannerProps['justifyContent']
  alignItems?: IBannerProps['alignItems']
  paddings?: IBannerProps['paddings']
  backButtonIndent?: IBannerProps['backButtonIndent']
}>`
  --bannerPaddingBottom: var(${FIXED_INDENTS.INDENT_90});

  ${MEDIA.MIN_1240} {
    --bannerPaddingBottom: var(${FIXED_INDENTS.INDENT_140});
  }

  width: 100%;
  height: auto;
  max-height: calc(100vh - var(--bannerPaddingBottom));
  position: absolute;
  top: 0;
  bottom: var(--bannerPaginatorRowHeight, 0);
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: ${({ alignItems }) => alignItems || BannerAlignItems.Start};
  justify-content: ${({ justifyContent }) =>
    justifyContent || BannerJustifyContent.Flex_start};
  padding: ${({ paddings }) => (paddings?.length ? `var(${paddings}) ` : 0)};

  /* Back button styles */
  > ${ButtonSC} {
    position: absolute;
    top: ${({ backButtonIndent }) =>
      `var(${backButtonIndent ?? BANNER_BACK_BUTTON_INDENT.SIZE_20})`};
  }
`

export const BannerSC = styled.div<{
  width: IBannerProps['width']
  aspectRatio: IBannerProps['aspectRatio']
  cursor: IBannerProps['cursor']
  corners?: CardCorners
}>`
  background-color: var(${COLOR.BACKGROUND_PROGRAM_CARD});
  width: ${({ width }) => (width ? `var(${width})` : `0`)};
  max-width: 100%;
  aspect-ratio: ${({ aspectRatio }) =>
    aspectRatio ? `var(${aspectRatio})` : `0`};
  position: relative;
  border-radius: ${({ corners }) => `var(${corners})`};

  /* Solution for browser versions, where the 'aspect-ratio' property is not supported */
  @supports not (
    aspect-ratio: ${({ aspectRatio }) =>
        aspectRatio ? `var(${aspectRatio})` : `0`}
  ) {
    &::before {
      float: left;
      padding-top: ${({ aspectRatio }) => `calc(100% / var(${aspectRatio}))`};
      content: '';
    }

    &::after {
      display: block;
      content: '';
      clear: both;
    }
  }

  // styles for liveEvents details page
  height: ${({ aspectRatio }) =>
    aspectRatio === BANNER_ASPECT_RATIO.LIVE_EVENTS_BANNER ? '100%' : 'auto'};
  /*
   * Compact home banners (600–1023) keep a 580px Figma shell so the
   * description can sit in a dark band under the 16:9 artwork. The token is
   * 0px elsewhere, so this stays inert at 375 and ≥1024.
   */
  min-height: var(${BANNER_HEIGHT.DETAILS_BANNER_COMPACT});

  cursor: ${({ cursor }) => cursor || 'initial'};

  /*
   * Unlock is the locked banner's only action, and Figma 375 stretches it
   * across the description width. Every other state keeps a shrink-to-fit row
   * of actions, so this is scoped to the locked banner.
   */
  ${MEDIA.MAX_599} {
    &[data-locked='true'] ${ButtonGroupWrapper} {
      width: 100%;

      ${ButtonSC}[data-arcana-button='true'] {
        width: 100%;
      }
    }
  }

  ${MEDIA.MAX_1239_LANDSCAPE} {
    ${BannerContent} {
      min-height: calc(
        100% - var(${LAYOUT.LoaderHeight}) - var(
            --bannerPaginatorRowHeight,
            0px
          )
      ); //navigation loader heigth
    }
  }
`
export const PosterContainer = styled.div<{
  aspectRatio?: BANNER_ASPECT_RATIO | null
  noContentInside?: boolean
  blurred?: boolean
  isTrailer?: boolean
}>`
  /* Scaled past the box so the blur cannot fade the artwork's own edges in. */
  filter: ${({ blurred }) => (blurred ? 'blur(60px) brightness(0.6)' : 'none')};
  transform: ${({ blurred }) => (blurred ? 'scale(1.15)' : 'none')};
  width: ${({ aspectRatio }) =>
    aspectRatio === BANNER_ASPECT_RATIO.POSTER ? 'initial' : '100%'};
  height: 100%;
  aspect-ratio: ${({ aspectRatio }) =>
    aspectRatio === BANNER_ASPECT_RATIO.POSTER
      ? `var(${aspectRatio})`
      : 'initial'};

  ${MEDIA.MAX_1239} {
    width: 100%;
    /* Below 1240 the banner shell is taller than 16:9, so a stretched trailer
       would cover text below it. Keep the video at its own ratio, pinned to
       the top, per FP-4289. */
    height: ${({ isTrailer }) => (isTrailer ? 'auto' : '100%')};
    aspect-ratio: ${({ isTrailer }) =>
      isTrailer ? `var(${BANNER_ASPECT_RATIO.SIZE_50})` : 'initial'};

    /*
     * Compact home banners (600–1023) raise the same 580px floor details use,
     * so non-trailer artwork must also stay 16:9 at the top. Below 600 the
     * portrait shell is already filled; from 1024 the floor is 0 and the
     * 16:9 pin is a no-op against the landscape ratio.
     */
    ${MEDIA.MIN_600} {
      height: auto;
      aspect-ratio: var(${BANNER_ASPECT_RATIO.SIZE_50});
    }
  }

  position: absolute;
  top: ${({ noContentInside }) =>
    noContentInside ? `var(${NAVBAR_HEIGHT.DEFAULT})` : `0`};
  right: 0;

  ${PosterSC} {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${FallbackImageSC} {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(${COLOR.TEXT_ICON_CONSTANT_DARK});
    color: var(${COLOR.DISABLED});
  }

  ${PartnerProgramCoverDesktopSC} {
    display: none;

    ${MEDIA.MIN_600} {
      display: block;
    }
  }

  ${PartnerProgramCoverMobileSC} {
    display: block;

    ${MEDIA.MIN_600} {
      display: none;
    }
  }
`

export const LockedImage = styled.div<{
  aspectRatio?: BANNER_ASPECT_RATIO | null
}>`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  aspect-ratio: ${({ aspectRatio }) =>
    aspectRatio === BANNER_ASPECT_RATIO.POSTER
      ? `var(${aspectRatio})`
      : 'initial'};

  ${MEDIA.MIN_600} {
    width: initial;
  }

  /*
   * Compact home banners pin the locked artwork to 16:9 at the top so the
   * padlock can centre on that band instead of the 580px shell.
   */
  ${MEDIA.MAX_1239} {
    ${MEDIA.MIN_600} {
      width: 100%;
      height: auto;
      aspect-ratio: var(${BANNER_ASPECT_RATIO.SIZE_50});
    }
  }

  ${IconSC} {
    z-index: 1;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateY(-50%) translateX(-50%);
    background-color: var(${COLOR.SURFACE_OPACITY_DARK_25});
    border-radius: var(${CORNERS.CORNERS_ROUND});
    border: 0.2px solid var(${COLOR.SURFACE_OPACITY_LIGHT_15});
    padding: var(${FIXED_INDENTS.INDENT_40});
    font-size: ${IconSizes.smBig};

    /*
     * Below 600 the banner is portrait and the description sits at its foot,
     * so Figma 375 puts the padlock 80px below the top edge — above the title
     * block rather than centred on the shell, where it lands on the title.
     */
    ${MEDIA.MAX_599} {
      top: var(${FIXED_INDENTS.INDENT_200});
      transform: translateX(-50%);
    }

    ${MEDIA.MIN_768} {
      font-size: ${IconSizes.mBig};
      padding: var(${FIXED_INDENTS.INDENT_60});
    }

    /*
     * From 1024 the compact floor is gone and the shell is 16:9, so Figma
     * moves the padlock to the free right-hand side. 600–1023 keeps the
     * default centre on the pinned artwork band.
     */
    ${MEDIA.MIN_1024} {
      left: initial;
      right: 150px;
    }

    ${MEDIA.MIN_1240} {
      right: 160px;
    }

    ${MEDIA.MIN_1440} {
      font-size: ${IconSizes.xmBig};
      padding: var(${FIXED_INDENTS.INDENT_80});
    }

    ${MEDIA.MIN_1640} {
      right: 200px;
    }
  }
`

export const OverlayLayout: any = styled.div`
  display: ${({ overlayEnabled }) => (overlayEnabled ? 'block' : 'none')};
  width: ${({ aspectRatio }) =>
    aspectRatio === BANNER_ASPECT_RATIO.POSTER ? 'initial' : '100%'};
  height: 100%;
  aspect-ratio: ${({ aspectRatio }) =>
    aspectRatio === BANNER_ASPECT_RATIO.POSTER
      ? `var(${aspectRatio})`
      : 'initial'};
  position: absolute;
  top: 0;
  right: 0;
  background: ${({ overlay, overlayDirection }) =>
    overlay &&
    (overlayDirection === 'OVERLAY_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL_FULL' ||
      overlayDirection === 'OVERLAY_VERTICAL_FULL')
      ? `var(${overlay})`
      : BannerOverlay.None};

  ${MEDIA.MAX_1239} {
    width: 100%;
    height: 100%;
    aspect-ratio: initial;

    /*
     * Compact home banners (600–1023) keep the fade on the 16:9 artwork so it
     * does not wash the dark description band below. Below 600 the artwork
     * fills the portrait shell; from 1024 the floor is 0 and this pin matches
     * the landscape ratio.
     */
    ${MEDIA.MIN_600} {
      height: auto;
      aspect-ratio: var(${BANNER_ASPECT_RATIO.SIZE_50});
    }
  }

  &:before {
    display: ${({ overlayDirection }) =>
      overlayDirection === 'OVERLAY_VERTICAL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL' ||
      overlayDirection === 'OVERLAY_VERTICAL_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL_FULL'
        ? 'block'
        : 'none'};
    content: '';
    width: 100%;
    height: ${({ overlayHeight }) => `${overlayHeight || 0}%`};
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${({ overlayVertical }) => `var(${overlayVertical})`};
  }

  &:after {
    display: ${({ overlayDirection }) =>
      overlayDirection === 'OVERLAY_HORIZONTAL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL_FULL' ||
      overlayDirection === 'OVERLAY_HORIZONTAL_VERTICAL'
        ? 'block'
        : 'none'};
    content: '';
    width: ${({ overlayWidth }) => `${overlayWidth || 0}%`};
    transform: translateX(-1px);
    height: 100%;
    position: absolute;
    background: ${({ overlayHorizontal, partialOverlay }) =>
      partialOverlay ? `var(${partialOverlay})` : `var(${overlayHorizontal})`};
  }
`

export const BannerPosterBackgroundCoverSC = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
  }

  div {
    width: 100%;
    height: 100%;
    background-color: red;
  }
`
